import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import type { IndividualEmployeeExportPayload } from './buildIndividualEmployeeExport';

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function safeSheetName(name: string): string {
  return name.replace(/[\\/?*[\]]/g, '').slice(0, 31) || 'Sheet';
}

function buildFilename(payload: IndividualEmployeeExportPayload, ext: string): string {
  const base = `employee-report-${payload.employeeName.replace(/\s+/g, '_').toLowerCase()}`;
  return `${base}.${ext}`;
}

function appendHeaderBlock(rows: (string | number)[][], payload: IndividualEmployeeExportPayload) {
  rows.push(['Individual Employee Report']);
  rows.push(['Employee', payload.employeeName]);
  rows.push(['Role', payload.role]);
  rows.push(['Period', payload.periodLabel]);
  rows.push(['From Date', payload.fromDate]);
  rows.push(['To Date', payload.toDate]);
  rows.push([]);
}

export function exportIndividualEmployeeExcel(payload: IndividualEmployeeExportPayload) {
  const wb = XLSX.utils.book_new();

  // First tab: header details + all summary metric counts
  const summaryAoa: (string | number)[][] = [];
  appendHeaderBlock(summaryAoa, payload);
  summaryAoa.push(['Metric', 'Value']);
  payload.summaryMetrics.forEach((m) => {
    summaryAoa.push([m.label, String(m.value)]);
  });
  const summaryWs = XLSX.utils.aoa_to_sheet(summaryAoa);
  summaryWs['!cols'] = [{ wch: 28 }, { wch: 28 }];
  XLSX.utils.book_append_sheet(wb, summaryWs, 'Summary');

  // Subsequent tabs: detailed rows for each summary metric
  payload.detailSheets.forEach((sheet) => {
    const aoa: (string | number)[][] = [];
    appendHeaderBlock(aoa, payload);
    aoa.push([sheet.title]);
    aoa.push([]);
    aoa.push(sheet.headers);
    if (sheet.rows.length === 0) {
      aoa.push(sheet.headers.map((_, i) => (i === 0 ? 'No detail rows' : '')));
    } else {
      sheet.rows.forEach((row) => aoa.push(row));
    }
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!cols'] = sheet.headers.map((h) => ({ wch: Math.min(Math.max(h.length + 2, 14), 28) }));
    XLSX.utils.book_append_sheet(wb, ws, safeSheetName(sheet.title));
  });

  const buffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  triggerDownload(
    new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    }),
    buildFilename(payload, 'xlsx'),
  );
}

export function exportIndividualEmployeePdf(payload: IndividualEmployeeExportPayload) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const docWithTable = doc as jsPDF & { lastAutoTable?: { finalY: number } };

  // Page 1: header details + all summary counts
  doc.setFontSize(14);
  doc.text('Individual Employee Report', 14, 16);
  doc.setFontSize(10);
  doc.setTextColor(80);
  doc.text(`Employee: ${payload.employeeName}`, 14, 24);
  doc.text(`Role: ${payload.role}`, 14, 30);
  doc.text(`Period: ${payload.periodLabel}`, 14, 36);
  doc.text(`From Date: ${payload.fromDate}    To Date: ${payload.toDate}`, 14, 42);
  doc.setTextColor(0);

  autoTable(doc, {
    head: [['Metric', 'Value']],
    body: payload.summaryMetrics.map((m) => [m.label, String(m.value)]),
    startY: 48,
    styles: { fontSize: 9, cellPadding: 2 },
    headStyles: { fillColor: [59, 130, 246] },
    margin: { left: 14, right: 14 },
  });

  // Subsequent pages: detailed rows for each metric
  payload.detailSheets.forEach((sheet) => {
    doc.addPage();
    doc.setFontSize(13);
    doc.text(sheet.title, 14, 16);
    doc.setFontSize(9);
    doc.setTextColor(80);
    doc.text(`${payload.employeeName} · ${payload.periodLabel}`, 14, 22);
    doc.setTextColor(0);

    autoTable(doc, {
      head: [sheet.headers],
      body:
        sheet.rows.length > 0
          ? sheet.rows
          : [sheet.headers.map((_, i) => (i === 0 ? 'No detail rows' : ''))],
      startY: 28,
      styles: { fontSize: 8, cellPadding: 1.5 },
      headStyles: { fillColor: [59, 130, 246] },
      margin: { left: 14, right: 14 },
    });

    void docWithTable.lastAutoTable;
  });

  triggerDownload(doc.output('blob'), buildFilename(payload, 'pdf'));
}
